import { useEffect, useState } from 'react';
import { Async } from '../../components/Async.tsx';
import { DataTable, type Column } from '../../components/DataTable.tsx';
import { Button } from '../../components/ui/button.tsx';
import { EmptyState, Panel } from '../../components/States.tsx';
import { formatDateTime } from '../../lib/format.ts';
import { useT } from '../../lib/i18n.tsx';
import { usePageTitle } from '../../lib/page-title.ts';
import { useClients, type ClientsResponse } from '../../lib/queries.ts';
import { useDashboardParams } from '../../lib/url-state.ts';
import { Controls } from '../controls/Controls.tsx';

type Row = ClientsResponse['data']['rows'][number];

const PAGE_SIZE = 50;
const SEARCH_DELAY_MS = 300;

/** The text typed into the search box, applied after a short pause so each keystroke is not a request. */
function useDebounced(value: string): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [value]);
  return debounced;
}

export function ClientsPage() {
  const t = useT();
  usePageTitle(t.clients.title);
  const [params] = useDashboardParams();
  const [typed, setTyped] = useState('');
  const search = useDebounced(typed.trim());
  const [paging, setPaging] = useState({ key: '', offset: 0 });

  // A new period, filter or search starts again from the first page.
  const key = `${params.from}|${params.to}|${params.salespersonId.join()}|${params.sourceId.join()}|${search}`;
  const offset = paging.key === key ? paging.offset : 0;
  const clients = useClients(params, { limit: PAGE_SIZE, offset, search });
  const dash = t.common.dash;
  const text = (value: string | null): string => value ?? dash;

  const columns: Column<Row>[] = [
    {
      id: 'date',
      header: t.clients.columns.date,
      sortValue: (r) => r.createdAt,
      cell: (r) => formatDateTime(r.createdAt),
    },
    {
      id: 'name',
      header: t.clients.columns.name,
      sortValue: (r) => r.customerName ?? '',
      cell: (r) => r.customerName ?? t.clients.noName,
    },
    {
      id: 'source',
      header: t.clients.columns.source,
      sortValue: (r) => r.sourceName ?? '',
      cell: (r) => r.sourceName ?? t.common.unknown,
    },
    {
      id: 'region',
      header: t.clients.columns.region,
      sortValue: (r) => r.region ?? '',
      cell: (r) => text(r.region),
    },
    {
      id: 'purpose',
      header: t.clients.columns.purpose,
      cell: (r) => text(r.purchasePurpose),
    },
    {
      id: 'size',
      header: t.clients.columns.containerSize,
      sortValue: (r) => r.containerSize ?? '',
      cell: (r) => text(r.containerSize),
    },
    {
      id: 'timeframe',
      header: t.clients.columns.timeframe,
      cell: (r) => text(r.purchaseTimeframe),
    },
    {
      id: 'status',
      header: t.clients.columns.status,
      sortValue: (r) => r.outcome,
      cell: (r) => t.clients.outcome[r.outcome],
    },
    {
      id: 'manager',
      header: t.clients.columns.manager,
      sortValue: (r) => r.salespersonName ?? '',
      cell: (r) => r.salespersonName ?? t.common.unassigned,
    },
  ];

  return (
    <>
      <h1 className="text-2xl font-bold tracking-tight">{t.clients.title}</h1>
      <Controls meta={clients.data?.meta} compare={false} />
      <Panel title={t.clients.caption}>
        <p className="mb-3 text-sm text-muted-foreground">{t.clients.privacyNote}</p>
        <label className="mb-3 flex max-w-sm flex-col gap-1 text-sm">
          <span>{t.clients.searchLabel}</span>
          <input
            type="search"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder={t.clients.searchPlaceholder}
            maxLength={100}
            className="h-11 w-full rounded-md border border-input bg-card px-3 sm:w-auto lg:h-10"
          />
        </label>
        <Async query={clients}>
          {({ data }) => {
            if (data.total === 0)
              return <EmptyState title={t.common.noData} description={t.clients.empty} />;
            const first = data.offset + 1;
            const last = data.offset + data.rows.length;
            return (
              <>
                <DataTable
                  caption={t.clients.caption}
                  columns={columns}
                  rows={data.rows}
                  rowKey={(r) => r.leadId}
                />
                <nav
                  aria-label={t.clients.pagination}
                  className="mt-3 flex items-center justify-between gap-3 text-sm"
                >
                  <span>{t.clients.range(first, last, data.total)}</span>
                  <span className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={data.offset === 0}
                      onClick={() =>
                        setPaging({ key, offset: Math.max(0, data.offset - PAGE_SIZE) })
                      }
                    >
                      {t.clients.previous}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={last >= data.total}
                      onClick={() => setPaging({ key, offset: data.offset + PAGE_SIZE })}
                    >
                      {t.clients.next}
                    </Button>
                  </span>
                </nav>
              </>
            );
          }}
        </Async>
      </Panel>
    </>
  );
}
